import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plane, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    // Navigate to marketplace with search query
    navigate('/marketplace');
    // The marketplace page will handle the search via URL params if needed
  };

  return (
    <section className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center overflow-hidden">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1753781467329-416d05e7e477?w=1920&q=80')",
          }}
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background" />
      </div>

      {/* Floating drone icon */}
      <div className="absolute top-20 right-20 opacity-20 animate-float hidden lg:block">
        <Plane className="w-32 h-32 text-primary" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-5xl mx-auto text-center">
          {/* Main heading */}
          <h1 className={`text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight transition-all duration-700 delay-100 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            The #1 Industrial
            <br />
            <span className="text-gradient bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
              Drone Marketplace
            </span>
          </h1>

          {/* Subheading */}
          <p className={`text-xl sm:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            The world's largest managed marketplace connecting construction, film, inspection, 
            and agricultural sectors with verified drone vendors.
          </p>

          {/* CTA - Search Bar */}
          <form 
            onSubmit={handleSearch}
            className={`max-w-3xl mx-auto transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by drone model (e.g., DJI Matrice 350)..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 h-14 text-base bg-background/95 backdrop-blur border-2 border-primary/20 focus:border-primary"
                />
              </div>
              <Button 
                type="submit"
                size="lg" 
                className="h-14 px-8 text-base font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow transition-all"
              >
                Search UAVs
              </Button>
            </div>
          </form>

          {/* Stats */}
          <div className={`grid grid-cols-1 sm:grid-cols-3 gap-8 mt-20 transition-all duration-700 delay-400 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}>
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-bold text-primary mb-2">2,500+</div>
              <div className="text-muted-foreground font-medium">Listings Ready</div>
            </div>
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-bold text-accent mb-2">100+</div>
              <div className="text-muted-foreground font-medium">Countries</div>
            </div>
            <div className="text-center">
              <div className="text-4xl sm:text-5xl font-bold text-secondary mb-2">500+</div>
              <div className="text-muted-foreground font-medium">Verified Vendors</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-5" />
    </section>
  );
}